import { getAsset } from "./store";
import { decimateSamples, type GhostTrack } from "../set/ghostTracks";
import type { FlightSetDetail } from "../api/types";
import type { TrackSample } from "../export/track";

/**
 * Rebuild ghost tracks from the `${id}:set` + `${id}:set-track:*` blobs a
 * "Download for offline use" stored.
 *
 * Anything missing or corrupt is skipped — offline ghosts are context, not a
 * mountable surface, so a ghost simply doesn't appear (the same silent
 * per-sibling degrade `loadGhostTracks` does online). Never throws.
 */
export async function loadCachedGhostTracks(flightId: string): Promise<GhostTrack[]> {
  const setRec = await getAsset(`${flightId}:set`);
  if (setRec === null) return [];
  let members: FlightSetDetail["members"];
  try {
    const set = JSON.parse(await setRec.blob.text()) as FlightSetDetail | null;
    // Valid JSON of the wrong shape (`null`, an array, a half-written object)
    // must degrade like corrupt bytes, not throw out of the loader.
    if (!Array.isArray(set?.members)) return [];
    members = set.members;
  } catch {
    return [];
  }
  const ghosts: GhostTrack[] = [];
  for (const member of members) {
    if (member.flightId === flightId) continue;
    const rec = await getAsset(`${flightId}:set-track:${member.flightId}`);
    if (rec === null) continue;
    try {
      const json = JSON.parse(await rec.blob.text()) as { samples?: TrackSample[] };
      const latlngs = decimateSamples(json.samples ?? []);
      if (latlngs.length >= 2) {
        ghosts.push({ flightId: member.flightId, name: member.name, latlngs });
      }
    } catch {
      // Corrupt cached sibling track — skip it, keep the rest.
    }
  }
  return ghosts;
}
